import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/NavBar';

const partnerTypes = [
  'Boutique hotels',
  'Serviced apartments',
  'Guesthouses',
  'Short-stay hosts',
  'Local property managers'
];

const guestBenefits = [
  {
    title: 'City guides guests can use right away',
    description: 'Help guests find nearby attractions, landmarks, museums, and local highlights from their phone.'
  },
  {
    title: 'Audio stories while they explore',
    description: 'TravelMaker gives travelers AI-powered audio guides for cities, landmarks, and cultural places.'
  },
  {
    title: 'Helpful answers from Jasmine',
    description: 'Guests can chat with Jasmine, our AI travel assistant, when they want to know more about a place.'
  },
  {
    title: 'A simple benefit for your guests',
    description: 'We can give your guests a TravelMaker offer such as a free trial, city guide, itinerary pack, or discount.'
  }
];

const partnerBenefits = [
  {
    stat: 'Useful',
    title: 'Add value to the stay',
    description: 'Give guests a helpful travel companion they can use while exploring the city.'
  },
  {
    stat: 'Simple',
    title: 'Easy to share',
    description: 'Share TravelMaker through a QR code, welcome message, booklet, or front-desk recommendation.'
  },
  {
    stat: 'Flexible',
    title: 'Built around your guests',
    description: 'We can discuss the right guest offer and partnership details once we understand your property.'
  }
];

const steps = [
  {
    number: '01',
    title: 'Tell us about your guests',
    description: 'Share your property type, city, and what your guests usually ask for during their stay.'
  },
  {
    number: '02',
    title: 'We agree on a simple offer',
    description: 'That could be a free trial, city guide, itinerary pack, discount, or another useful TravelMaker benefit.'
  },
  {
    number: '03',
    title: 'You share it with guests',
    description: 'We provide the link or QR code, and you share it wherever it naturally fits your guest experience.'
  }
];

const faqs = [
  {
    question: 'Who is this for?',
    answer: 'Hotels, guesthouses, serviced apartments, short-stay hosts, and local accommodation teams who want to help guests explore the city.'
  },
  {
    question: 'What would guests receive?',
    answer: 'Guests can receive access to TravelMaker with a special benefit, such as a free trial, city guide, itinerary pack, or discount.'
  },
  {
    question: 'How do we share it?',
    answer: 'Most partners can share TravelMaker through a QR code, welcome booklet, check-in email, reception desk, or guest message.'
  },
  {
    question: 'How do partnership details work?',
    answer: 'We keep this flexible. Once we understand your property and guests, we can discuss the best setup together.'
  }
];

const shareMethods = [
  {
    title: 'In-room QR cards',
    description: 'Place a small card by the welcome book, desk, or coffee station so guests can scan when planning their day.',
    icon: (
      <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none" aria-hidden="true">
        <rect x="8" y="8" width="12" height="12" rx="3" fill="currentColor" />
        <rect x="28" y="8" width="12" height="12" rx="3" fill="currentColor" />
        <rect x="8" y="28" width="12" height="12" rx="3" fill="currentColor" />
        <path d="M29 29h4v4h-4v-4Zm7 0h4v11h-4V29Zm-7 7h4v4h-4v-4Z" fill="currentColor" />
      </svg>
    )
  },
  {
    title: 'Welcome booklets',
    description: 'Add TravelMaker beside your house rules, restaurant tips, and local highlights.',
    icon: (
      <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none" aria-hidden="true">
        <path d="M10 11h13c3.3 0 6 2.7 6 6v20c0-3.3-2.7-6-6-6H10V11Z" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" />
        <path d="M38 11h-9v26c0-3.3 2.7-6 6-6h3V11Z" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" />
        <path d="M15 18h8M15 25h7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    )
  },
  {
    title: 'Post check-in emails',
    description: 'Send the guest offer after arrival, when they are actively deciding what to do nearby.',
    icon: (
      <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none" aria-hidden="true">
        <rect x="7" y="12" width="34" height="25" rx="5" stroke="currentColor" strokeWidth="4" />
        <path d="m10 17 14 11 14-11" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M34 8v8M30 12h8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
    )
  },
  {
    title: 'Reception or concierge desks',
    description: 'Give front-desk teams a fast recommendation tool for guests asking what to see next.',
    icon: (
      <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none" aria-hidden="true">
        <path d="M8 35h32M13 35v-8a11 11 0 0 1 22 0v8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 15a5 5 0 1 1 6 0" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        <path d="M10 40h28" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
    )
  }
];

const contactHref = 'mailto:info@travelmaker.app?subject=TravelMaker%20partnership%20enquiry';

const Partners = () => {
  useEffect(() => {
    document.title = 'Partner with TravelMaker | Guest Travel Guides for Hosts & Hotels';
    let metaDescription = document.querySelector('meta[name="description"]');

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute(
      'content',
      'Partner with TravelMaker to give hotel guests, short-stay guests, and travelers a helpful AI-powered city guide, audio guide, and travel companion.'
    );
  }, []);

  return (
    <>
      <NavBar />
      <main className="bg-white text-gray-900">
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-teal-50/70 to-blue-50 pt-28 pb-24">
        <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
          <div>
            <div className="inline-flex items-center rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-primary shadow-sm border border-primary/10 mb-8">
              For hosts, hotels, and guest experience teams
            </div>

            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-7">
              Give your guests a smarter way to explore your city.
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-2xl">
              TravelMaker helps your guests discover nearby attractions, listen to AI-powered audio guides,
              chat with Jasmine, and make the most of their stay from the moment they arrive.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href={contactHref}
                className="inline-flex justify-center items-center rounded-xl bg-primary px-7 py-4 text-white font-semibold shadow-lg shadow-primary/20 transition-all duration-300 hover:-translate-y-1 hover:bg-primary-dark"
              >
                Partner with us
              </a>
              <Link
                to="/contact"
                className="inline-flex justify-center items-center rounded-xl border-2 border-primary/20 bg-white px-7 py-4 text-primary font-semibold transition-all duration-300 hover:-translate-y-1 hover:border-primary"
              >
                Ask us a question
              </Link>
            </div>

            <div className="flex flex-wrap gap-3">
              {partnerTypes.map((type) => (
                <span key={type} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm border border-gray-100">
                  {type}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rotate-3 rounded-[2rem] bg-gradient-to-br from-primary/20 to-blue-500/20"></div>
            <div className="relative rounded-[2rem] bg-white p-6 md:p-8 shadow-2xl border border-white/80">
              <div className="rounded-3xl bg-gray-900 p-5 text-white mb-6">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <p className="text-white/60 text-sm">Guest guide</p>
                    <h2 className="text-2xl font-bold">A perfect day nearby</h2>
                  </div>
                  <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center font-bold">TM</div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-sm text-white/60 mb-1">Morning</p>
                    <p className="font-semibold">Start with a landmark audio guide</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-sm text-white/60 mb-1">Afternoon</p>
                    <p className="font-semibold">Follow local tips and nearby highlights</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4">
                    <p className="text-sm text-white/60 mb-1">Anytime</p>
                    <p className="font-semibold">Ask Jasmine what to do next</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-[120px_1fr] gap-5 items-center">
                <div className="rounded-2xl bg-gray-100 p-3">
                  <div className="grid grid-cols-4 gap-1">
                    {Array.from({ length: 32 }).map((_, index) => (
                      <div
                        key={index}
                        className={`aspect-square rounded-sm ${index % 3 === 0 || index % 7 === 0 ? 'bg-gray-900' : 'bg-white'}`}
                      ></div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary mb-1">Scan to unlock</p>
                  <h3 className="text-2xl font-bold mb-2">A TravelMaker offer for your guests</h3>
                  <p className="text-gray-600 leading-relaxed">
                    A simple QR code can help guests unlock a guide, trial, itinerary pack, or discount.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-14 items-start">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">Turn local tips into a better guest experience.</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Guests often ask where to eat, what to visit, how to get around, and what is worth their time.
                TravelMaker gives you a polished way to share helpful recommendations while giving guests a richer city experience.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {guestBenefits.map((benefit) => (
                <div key={benefit.title} className="rounded-3xl bg-gray-50 p-7 border border-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">A simple way to help guests explore</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Your guests are already looking for things to do. TravelMaker gives them a helpful companion
              for discovering the city, listening to stories, and learning more as they move around.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnerBenefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl bg-white p-8 shadow-lg border border-gray-100">
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary mb-5">{benefit.stat}</p>
                <h3 className="text-2xl font-semibold mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
            <div className="rounded-[2rem] bg-gradient-to-br from-primary to-blue-600 p-8 md:p-10 text-white shadow-2xl">
              <h2 className="text-4xl font-bold mb-6">How it works</h2>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                If you are interested, get in touch. We can discuss your property, your guests,
                and the best way to offer TravelMaker to them.
              </p>
              <a
                href={contactHref}
                className="inline-flex rounded-xl bg-white px-7 py-4 font-semibold text-primary transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                Contact us
              </a>
            </div>

            <div className="space-y-5">
              {steps.map((step) => (
                <div key={step.number} className="grid grid-cols-[72px_1fr] gap-5 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Simple, guest-friendly ways to share</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              TravelMaker works best when it appears naturally at the moments guests are already looking for help.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {shareMethods.map((method) => (
              <div key={method.title} className="group rounded-3xl bg-white p-7 shadow-sm border border-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-white">
                  {method.icon}
                </div>
                <h3 className="font-semibold text-xl mb-3">{method.title}</h3>
                <p className="text-gray-600 leading-relaxed">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Common questions</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              The details can stay simple for now. If this sounds useful for your guests, we would love to talk.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-3xl border border-gray-100 bg-gray-50 p-7">
                <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 bg-gradient-to-br from-gray-900 via-gray-900 to-primary text-white">
        <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-white/10 blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">Want to try TravelMaker with your guests?</h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10">
            Tell us about your property, city, and guest profile. We will help shape a simple offer
            that gives your guests a better way to explore.
          </p>
          <a
            href={contactHref}
            className="inline-flex rounded-xl bg-white px-8 py-4 font-semibold text-gray-900 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            Email us about partnering
          </a>
        </div>
      </section>
      </main>
    </>
  );
};

export default Partners;
